<template>
  <!-- วางแผนเส้นทาง: the planner's days for one fixed trip. It plans, re-times
       and collects; the page saves (US.savePlan) and writes SAP's dates.
       A trip already sent opens read-only (ดูเส้นทาง): its saved plan re-timed,
       for the map links, with nothing to move or save. -->
  <v-dialog :value="value" max-width="1000" scrollable persistent @input="$emit('input', $event)">
    <v-card v-if="trip">
      <v-card-title>{{ readonly ? 'เส้นทาง (ส่งเข้าจัดรถแล้ว)' : 'แผนเส้นทาง' }} · รถ {{ trip.regNum }} · ออก {{ trip.startDate }}</v-card-title>
      <v-card-subtitle class="pt-1">{{ groupText }} · {{ orders.length }} ออเดอร์</v-card-subtitle>
      <v-card-text>
        <v-progress-linear v-if="busy" indeterminate class="mb-2" />
        <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
        <template v-if="plan">
          <!-- The saved plan, refitted: orders added or removed since it was saved. -->
          <v-alert v-if="changes" type="info" dense outlined>
            แผนที่บันทึกไว้ ปรับตามรายการปัจจุบัน — ตรวจแล้วกด บันทึกแผน (หรือ วางแผนใหม่ เพื่อจัดใหม่ทั้งรอบ)
            <div v-for="t in changeLines" :key="t" class="caption">{{ t }}</div>
          </v-alert>
          <v-alert v-if="plan.unplaceable.length" type="warning" dense>
            หาตำแหน่งไม่ได้ {{ plan.unplaceable.length }} ออเดอร์ ไม่อยู่ในแผน (ยังส่งวันออกรถ) — แก้ที่อยู่หรือพิกัดของ SO ใน SAP แล้วกด อัปเดตจาก SAP ก่อนวางแผนใหม่:
            {{ plan.unplaceable.map(refOf).join(', ') }}
          </v-alert>
          <div v-for="(d, di) in plan.days" :key="d.date" class="mb-3">
            <div class="d-flex flex-wrap align-center" style="gap: 8px">
              <strong>วันที่ {{ di + 1 }} · {{ dayLabel(d.date) }}</strong>
              <span class="caption">ออก {{ d.start || '-' }} · {{ d.km }} กม.<template v-if="d.end"> · เสร็จ {{ d.end }}</template></span>
              <v-chip v-if="(d.flags || []).indexOf('KM_OVER') !== -1" x-small color="orange" text-color="white">
                เกินระยะแนะนำ {{ d.km }} / {{ d.kmRecommended }} กม.</v-chip>
              <!-- A Maps directions link holds nine waypoints; a longer day comes in parts. -->
              <a v-for="(url, ui) in routeParts(d.date)" :key="ui" :href="url" target="_blank" rel="noopener" class="caption text-no-wrap">
                <v-icon small color="primary">mdi-google-maps</v-icon>{{ routeParts(d.date).length > 1 ? `ส่วน ${ui + 1}/${routeParts(d.date).length}` : 'ดูเส้นทางวันนี้' }}</a>
            </div>
            <div v-if="!d.stops.length" class="caption grey--text">ขับรถทั้งวัน — ยังไม่ถึงจุดส่ง</div>
            <!-- One table per day, so the columns are fixed: sized by content, a day
                 without ไม่มีพิกัด chips came out narrower than the day above it. -->
            <v-simple-table v-else dense class="plan-day">
              <colgroup>
                <col style="width: 36px">
                <col style="width: 120px">
                <col>
                <col style="width: 80px">
                <col style="width: 150px">
                <col :style="{ width: readonly ? '40px' : '150px' }">
              </colgroup>
              <tbody>
                <tr v-for="(s, si) in d.stops" :key="s.orderId" :class="{ 'plan-new': isNew(s) }">
                  <td class="text-no-wrap">{{ si + 1 }}</td>
                  <td class="text-no-wrap">{{ s.arrive }} → {{ s.leave }}
                    <div v-if="s.wait" class="caption grey--text">รอ {{ s.wait }} นาที</div></td>
                  <td>{{ nameOf(s.orderId) }}
                    <div class="caption grey--text">{{ refOf(s.orderId) }} · {{ provinceOf(s.orderId) }}</div>
                    <div v-if="addressOf(s.orderId)" class="caption">{{ addressOf(s.orderId) }}</div>
                    <!-- One drop, several SOs at the same place: each further member, its address only when it differs. -->
                    <div v-for="id in othersOf(s)" :key="id" class="caption">+ {{ refOf(id) }} · {{ nameOf(id) }}<template
                      v-if="addressOf(id) && addressOf(id) !== addressOf(s.orderId)"> · {{ addressOf(id) }}</template></div></td>
                  <td class="text-no-wrap caption">{{ s.km }} กม.</td>
                  <td>
                    <v-chip v-if="isNew(s)" x-small color="blue" text-color="white" class="mr-1">ใหม่</v-chip>
                    <v-chip v-if="s.shop" x-small class="mr-1">ร้านค้า</v-chip>
                    <v-chip v-for="f in s.flags" :key="f" x-small :color="f === 'NO_PIN' ? 'grey' : 'red'" text-color="white" class="mr-1">
                      {{ flagText(f, s, d) }}</v-chip>
                  </td>
                  <td class="text-no-wrap">
                    <v-btn icon x-small title="ดูตำแหน่งใน Google Maps" :href="placeOf(s)" target="_blank" rel="noopener" :disabled="!placeOf(s)">
                      <v-icon small>mdi-map-marker</v-icon></v-btn>
                    <template v-if="!readonly">
                    <v-btn icon x-small title="เลื่อนขึ้น" :disabled="busy" @click="move(s.orderId, 'up')"><v-icon small>mdi-arrow-up</v-icon></v-btn>
                    <v-btn icon x-small title="เลื่อนลง" :disabled="busy" @click="move(s.orderId, 'down')"><v-icon small>mdi-arrow-down</v-icon></v-btn>
                    <v-btn icon x-small title="ไปวันก่อนหน้า" :disabled="busy || di === 0" @click="move(s.orderId, 'prevDay')"><v-icon small>mdi-chevron-double-up</v-icon></v-btn>
                    <v-btn icon x-small title="ไปวันถัดไป" :disabled="busy" @click="move(s.orderId, 'nextDay')"><v-icon small>mdi-chevron-double-down</v-icon></v-btn>
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <v-divider class="my-2" />
          <div>ตามแผน รถกลับถึงคลัง <strong>{{ dayLabel(plan.home.arriveDate) }} {{ plan.home.arrive }}</strong> · {{ plan.home.km }} กม. · รวมทั้งรอบ {{ plan.km }} กม.</div>
          <div>จองรถไว้ถึง <strong>{{ dayLabel(trip.returnDate) }}</strong></div>
          <!-- Two dates, one choice: a checkbox labelled "use the suggested date" read
               as an instruction to tick it. Earlier than booked keeps the booking by
               default (ปิดรอบ frees an early truck); later than booked moves it. -->
          <!-- A sent trip: no choice -- only a later return is written, with the truck's booking. -->
          <v-alert v-if="!readonly && sent && longerReturn" dense type="warning" class="mt-2 mb-0">
            บันทึกแล้วจะเลื่อนวันกลับเป็น {{ dayLabel(longerReturn) }} และจองรถถึงวันนั้น</v-alert>
          <template v-if="!readonly && !sent && plan.suggestedReturn !== trip.returnDate">
            <v-alert v-if="late" dense type="warning" class="mt-2 mb-0">
              แผนกลับช้ากว่าวันที่จองรถไว้ {{ gapDays }} วัน — ควรเลื่อนวันกลับ ไม่เช่นนั้นรถอาจถูกจัดรอบอื่นซ้อน</v-alert>
            <v-radio-group v-model="useReturn" dense hide-details class="mt-2">
              <template v-if="late">
                <v-radio :value="true" :label="`เปลี่ยนวันกลับเป็น ${dayLabel(plan.suggestedReturn)} (แนะนำ)`" />
                <v-radio :value="false" :label="`คงวันกลับเดิม ${dayLabel(trip.returnDate)} — รถอาจถูกจัดรอบอื่นซ้อน`" />
              </template>
              <template v-else>
                <v-radio :value="false" :label="`คงวันกลับ ${dayLabel(trip.returnDate)} — เผื่อเวลา ${gapDays} วัน · ถ้ารถกลับก่อน กด ปิดรอบ รถว่างทันที`" />
                <v-radio :value="true" :label="`เปลี่ยนวันกลับเป็น ${dayLabel(plan.suggestedReturn)} — รถรับรอบอื่นได้เร็วขึ้น แต่ไม่มีวันเผื่อ`" />
              </template>
            </v-radio-group>
          </template>
          <div v-if="edited" class="caption mt-1">แก้ลำดับเองแล้ว — กด วางแผนใหม่ ให้ระบบจัดให้อีกครั้ง</div>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-btn v-if="!readonly" text :disabled="busy" @click="replan">วางแผนใหม่</v-btn>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">ปิด</v-btn>
        <v-btn v-if="!readonly" color="primary" :disabled="!plan || busy" :loading="saving" @click="$emit('submit', { plan, useReturn: sent ? false : useReturn })">บันทึกแผน</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import U from '@/components/upcountry'
import P from '@/components/upcountryPlan'
import R from '@/components/bookingRules'
import { planUpTrip, retimeUpTrip, refitUpTrip } from '@/components/plannerClient'
import { reportError } from '@/components/report'

export default {
  name: 'upcountryPlanDialog',
  props: {
    value: Boolean,
    trip: { type: Object, default: null },
    orders: { type: Array, default: () => [] },
    closed: { type: Object, default: () => ({}) },
    planCfg: { type: Object, default: null },
    today: { type: String, default: '' },
    saving: Boolean
  },
  data () {
    return { plan: null, busy: false, error: '', edited: false, useReturn: false, runId: 0, changes: null }
  },
  computed: {
    byId () {
      const m = {}
      this.orders.forEach(o => { m[o.id] = o })
      return m
    },
    // The banner's lines: where each added order landed, and what left.
    changeLines () {
      if (!this.changes || !this.plan) return []
      const where = {}
      this.plan.days.forEach((d, di) => d.stops.forEach((s, si) => {
        const ids = s.orderIds || [s.orderId]
        ids.forEach(id => { where[id] = { di, si, joined: ids.length > 1 } })
      }))
      const lines = this.changes.added.map(id => {
        const w = where[id]
        const at = !w ? 'หาตำแหน่งไม่ได้ ไม่อยู่ในแผน'
          : `${w.joined ? 'รวมในจุดที่' : 'แทรกเป็นจุดที่'} ${w.si + 1} วันที่ ${w.di + 1}`
        return `เพิ่มหลังบันทึกแผน: ${this.refOf(id)} · ${this.nameOf(id)} (${at})`
      })
      if (this.changes.removed.length) lines.push(`นำออกจากรอบหลังบันทึกแผน ${this.changes.removed.length} ออเดอร์ — ไม่อยู่ในแผนแล้ว`)
      return lines
    },
    // The plan brings the truck back after the date it is booked to.
    late () { return !!(this.plan && this.trip && this.plan.suggestedReturn > this.trip.returnDate) },
    gapDays () {
      if (!this.plan || !this.trip) return 0
      return Math.abs(Math.round((Date.parse(this.plan.suggestedReturn) - Date.parse(this.trip.returnDate)) / 86400000))
    },
    // A fixed trip can be re-planned, and a sent one whose orders changed while
    // it is still before its loading day is over; otherwise the route is keyed in.
    readonly () {
      const t = this.trip
      if (!t) return false
      return t.status !== 'fixed' && !(t.status === 'sent' && U.checkSentEdit(t, this.today || R.todayStr()).ok && !P.planIsCurrent(t))
    },
    sent () { return !!(this.trip && this.trip.status === 'sent') },
    orderKey () { return this.trip ? (this.trip.orderIds || []).slice().sort().join(',') + '|' + this.orders.length : '' },
    longerReturn () { return this.plan && this.trip ? P.longerReturn(this.trip, this.plan) : null },
    groupText () { return ((this.trip && this.trip.groups) || []).map(U.groupLabel).join(', ') },
    routeByDate () {
      const m = {}
      if (this.plan) P.dayRouteUrls(this.plan).forEach(r => { m[r.date] = r.parts })
      return m
    }
  },
  watch: {
    value (open) { if (open) this.start() },
    // The page opens this right after + เพิ่มออเดอร์ on a sent trip, and the
    // trip's listener can deliver the new orders a moment later: start again
    // on the fresh trip, or the dialog would show the old plan read-only.
    orderKey (now, was) { if (this.value && this.trip && now !== was) this.start() }
  },
  methods: {
    // Plain copies: the worker receives everything by structured clone.
    input (extra) {
      return JSON.parse(JSON.stringify(Object.assign({ trip: this.trip, orders: this.orders, closed: this.closed, cfg: this.planCfg }, extra)))
    },
    async run (fn, input) {
      // A result that lands after the dialog was reopened or a newer run began is dropped.
      const id = ++this.runId
      this.busy = true
      this.error = ''
      try {
        const plan = Object.freeze(await fn(input))
        if (id !== this.runId) return
        this.plan = plan
        // Past the trip's return date: the suggested return is the honest default (staff may untick it).
        if (plan.warnings && plan.warnings.indexOf('PAST_RETURN') !== -1) this.useReturn = true
      } catch (e) {
        if (id !== this.runId) return
        reportError('upcountry plan', e)
        this.error = 'วางแผนไม่สำเร็จ — ' + (e && e.message ? e.message : String(e))
      } finally {
        if (id === this.runId) this.busy = false
      }
    },
    // A saved plan always opens: as saved (re-timed, for its flags and km), or,
    // when orders were added or removed since, refitted around staff's order.
    start () {
      this.plan = null
      this.edited = false
      this.useReturn = false
      this.changes = null
      if (this.readonly) {
        if (this.trip.plan) return this.run(retimeUpTrip, this.input({ sequence: P.sequenceOf(this.trip.plan) }))
        this.error = 'รอบนี้ไม่มีแผนเส้นทางที่บันทึกไว้'
        return
      }
      if (P.planIsCurrent(this.trip)) return this.run(retimeUpTrip, this.input({ sequence: P.sequenceOf(this.trip.plan) }))
      if (this.trip && this.trip.plan) {
        return this.run(refitUpTrip, this.input({ stored: this.trip.plan })).then(() => {
          if (this.plan && this.plan.changes) this.changes = this.plan.changes
        })
      }
      return this.replan()
    },
    replan () {
      this.edited = false
      this.changes = null
      return this.run(planUpTrip, this.input({}))
    },
    isNew (s) { return !!this.changes && (s.orderIds || [s.orderId]).some(id => this.changes.added.indexOf(id) !== -1) },
    move (orderId, dir) {
      this.edited = true
      return this.run(retimeUpTrip, this.input({ sequence: P.moveStop(P.sequenceOf(this.plan), orderId, dir) }))
    },
    dayLabel (ymd) { return `${P.DOW_TH[P.weekday(ymd)]} ${ymd}` },
    nameOf (id) { return (this.byId[id] || {}).name || id },
    refOf (id) { return this.byId[id] ? U.orderRef(this.byId[id]) : id },
    provinceOf (id) { return (this.byId[id] || {}).upProvince || '' },
    addressOf (id) { const o = this.byId[id] || {}; return o.address || o.custAddress || '' },
    othersOf (s) { return (s.orderIds || [s.orderId]).filter(id => id !== s.orderId) },
    placeOf (s) { return P.placeUrl(s, this.byId[s.orderId]) || undefined },
    routeParts (date) { return this.routeByDate[date] || [] },
    flagText (f, s, d) {
      if (f === 'CLOSED_DAY') return 'ร้านปิดวัน' + P.DOW_TH[P.weekday(d.date)]
      if (f === 'WINDOW') return s.shop ? 'เกินเวลารับของของร้าน' : 'เสร็จเกินเวลา'
      return 'ไม่มีพิกัด'
    }
  }
}
</script>

<style scoped>
.plan-day ::v-deep table {
  table-layout: fixed;
}
.plan-new {
  background: rgba(33, 150, 243, 0.08);
}
</style>
